import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CollapsibleSection from '../CollapsibleSection';
import MessageModal from '../MessageModal';

const PendingQuotesSection = ({ quotes = [], loading = false }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState(null);

  const handleFollowUp = (quote) => {
    console.log('PendingQuotes: Follow up clicked for quote', quote.id, {
      clientEmail: quote.clientEmail,
      quote
    });

    // Set up email modal data for quote_followup template
    setEmailModalData({
      templateType: 'quote_followup',
      recipientEmail: quote.clientEmail || '',
      contextData: {
        client_name: quote.clientName,
        project_name: quote.projectName,
        quote_link: quote.publicAccessToken ?
          `${window.location.origin}/quotes/public/${quote.publicAccessToken}` :
          `${window.location.origin}/quotes/view/${quote.id}`
      },
      quoteId: quote.id,
      opportunityId: null
    });
    setShowEmailModal(true);
  };

  const handleViewQuote = (quote) => {
    // Navigate to the client's public view of the quote
    if (quote.publicAccessToken) {
      window.location.href = `/quotes/public/${quote.publicAccessToken}`;
    } else {
      // Fallback if publicAccessToken is not available
      console.warn('Quote does not have a public access token', quote);
      window.location.href = `/quotes/view/${quote.id}`;
    }
  };

  const QuoteCard = ({ quote }) => (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{quote.clientName}</h4>
          <p className="text-sm text-gray-600 truncate">{quote.projectName}</p>
        </div>
        <span className="text-lg font-bold text-gray-900 flex-shrink-0">
          ${quote.amount?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
        </span>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <span className="text-xs text-gray-500">
          {quote.daysAgo ? `Sent ${quote.daysAgo}d ago` : 'Recently sent'}
        </span>
        <div className="flex gap-2">
          <button
            onClick={() => handleFollowUp(quote)}
            className="text-xs text-blue-600 hover:text-blue-900 font-medium"
          >
            Follow up
          </button>
          <button
            onClick={() => handleViewQuote(quote)}
            className="text-xs text-gray-600 hover:text-gray-900 font-medium"
          >
            View
          </button>
        </div>
      </div>
    </div>
  );

  const loadingCards = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
          <div className="flex justify-between mb-2">
            <div className="h-5 bg-gray-200 rounded w-1/2"></div>
            <div className="h-5 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className="text-center py-8">
      <p className="text-gray-500">No pending quotes</p>
    </div>
  );

  const quotesList = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {quotes.map((quote) => (
        <QuoteCard key={quote.id} quote={quote} />
      ))}
    </div>
  );

  return (
    <>
      <CollapsibleSection
        title="Pending Quotes"
        icon={
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        }
        badge={loading ? '...' : quotes.length}
        defaultOpen={true}
      >
        {loading ? loadingCards : quotes.length === 0 ? emptyState : quotesList}
      </CollapsibleSection>

      {/* Email Modal for sending quote follow-up */}
      {showEmailModal && emailModalData && (
        <MessageModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEmailModalData(null);
          }}
          templateType={emailModalData.templateType}
          recipientEmail={emailModalData.recipientEmail}
          contextData={emailModalData.contextData}
          quoteId={emailModalData.quoteId}
          onSuccess={() => {
            setShowEmailModal(false);
            setEmailModalData(null);
          }}
        />
      )}
    </>
  );
};

export default PendingQuotesSection;
